"use client"

import { LocationDisplay } from "@/components"
import { BreadCrumbs } from "@/components/BreadCrumbs"
import { ConnectionsPreview } from "@/components/ConnectionsPreview"
import { IconButton, Text } from "@/elements"
import Avatar from "@/elements/Avatar/Avatar"
import { FragmentType, graphql, useFragment } from "@/gql"
import { PortfolioHeaderFragmentDoc } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import clsx from "clsx"
import React from "react"
import { PortfolioUIState } from "../page"
import { HeaderActionButtons } from "./HeaderActionButtons"
import { PortfolioEditModal, useEditPortfolio } from "./edit/useEditPortfolio"
import { PencilIcon } from "@heroicons/react/24/outline"

export const PortfolioHeaderFragment = graphql(`
  fragment PortfolioHeader on IndividualCreative {
    id
    __typename
    handle
    locationName @skip(if: $displayingPrivateProfile)
    professionString
    displayName
    user {
      id
      avatarInitials
      name
      profile {
        id
        avatar390w: avatarUrl(width: 390)
      }
      ...ConnectionsPreviewV1 @skip(if: $displayingPrivateProfile)
    }

    ...CreativeAvatarWithNameAndTitle
    ...HeaderActionButtons @skip(if: $displayingPrivateProfile)
  }
`)

interface Props {
  creative: FragmentType<typeof PortfolioHeaderFragmentDoc> | undefined | null
  className?: string
  size: "sm" | "lg"
  uiState: PortfolioUIState
  serverSession: NovaSession | null
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

const editingClasses = "cursor-pointer bg-bg-2 hover:bg-bg-3 p-1 rounded-sm"

export const PortfolioHeaderDesktop: React.FC<Props> = props => {
  const { size = "lg", uiState, serverSession, editProfileMethods } = props
  const creative = useFragment(PortfolioHeaderFragmentDoc, props.creative)
  const isLarge = size === "lg"
  const avatarSize = isLarge ? "4xl" : "3xl"
  const user = creative?.user
  const name = creative?.displayName || user?.name
  const initials = user?.avatarInitials
  const avatarImg = user?.profile?.avatar390w
  const location = creative?.locationName
  const professionString = creative?.professionString

  return (
    <>
      <div className="flex w-full flex-col">
        {uiState.isAuthenticated && (
          <div className="flex w-full flex-row items-center justify-between pb-8">
            <BreadCrumbs />
          </div>
        )}
        <div className="flex flex-row items-center">
          <div className="flex w-full flex-1 flex-row items-center">
            <div className="relative size-[120px]">
              <Avatar initials={initials} src={avatarImg} size={avatarSize} />
              {uiState.isUsersPortfolio && editProfileMethods?.isEditing && (
                <div className="absolute right-0 top-0">
                  <IconButton
                    Icon={PencilIcon}
                    floating
                    onClick={() => editProfileMethods?.setOpenModal(PortfolioEditModal.UpdateAvatarImage)}
                  />
                </div>
              )}
            </div>
            <div className={clsx("flex flex-col", isLarge ? "ml-6" : "ml-4")}>
              <Text
                onClick={() =>
                  editProfileMethods?.isEditing ? editProfileMethods?.setOpenModal(PortfolioEditModal.EditName) : null
                }
                variant="h5"
                element="h1"
                className={clsx("flex-grow-0", editProfileMethods?.isEditing && editingClasses)}>
                {name}
              </Text>
              <Text
                onClick={() =>
                  editProfileMethods?.isEditing ? editProfileMethods?.setOpenModal(PortfolioEditModal.EditRoles) : null
                }
                variant="body1"
                element="h2"
                maxLines={2}
                className={clsx("mt-1 flex-grow-0 text-fg-2", editProfileMethods?.isEditing && editingClasses)}>
                {professionString}
              </Text>
              {uiState.isPublic && uiState.isAuthenticated && (
                <div className="mt-4 flex flex-row items-center gap-5">
                  <LocationDisplay
                    onClick={() =>
                      editProfileMethods?.isEditing
                        ? editProfileMethods?.setOpenModal(PortfolioEditModal.EditLocation)
                        : null
                    }
                    location={location}
                    className={clsx("", editProfileMethods?.isEditing && editingClasses)}
                  />
                  <ConnectionsPreview user={creative?.user} textVariant="body3" serverSession={serverSession} />
                </div>
              )}
            </div>
          </div>
          {uiState.isAuthenticated && (
            <div className="ml-4 flex h-full flex-shrink-0 flex-row">
              <HeaderActionButtons
                editProfileMethods={editProfileMethods}
                creative={creative}
                serverSession={serverSession}
                uiState={uiState}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
